import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {
  SchemaResponse,
  SchemasResponse,
  FullSchemaResponse,
  SchemaCreate,
  SchemaUpdate,
  UpdateDayBody,
  SchemaSceneResponse,
  SchemaSceneGroupResponse,
  SchemaUserResponse,
  CreatedSchemaResponse,
  FileResponse,
  DownloadLinkResponse,
} from '@backend/schemas/schemasRouter'
import { SearchParam } from '@backend/types'
import { IId, ISchemaId } from '../../types/common'
import { prepareHeaders, handleApiError } from './helpers'

export interface ISchema extends SchemaResponse {}

export interface ISchemaCreate extends SchemaCreate {}

export interface ISchemaUpdate extends SchemaUpdate {}

export interface IUpdateDay extends UpdateDayBody {}

export interface ICreatedSchema extends CreatedSchemaResponse {}

export interface IFile extends FileResponse {}

export interface IUser extends SchemaUserResponse {}

export interface IFullSchema {
  schema: FullSchemaResponse
  scenes: SchemaSceneResponse[]
  sceneGroups: SchemaSceneGroupResponse[]
  users: SchemaUserResponse[]
}

export const schemasApi = createApi({
  reducerPath: 'schemas',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getSchemas: builder.query<SchemasResponse, SearchParam>({
      query: ({ limit, offset, sort, sortKey, key, value }) => ({
        url: 'schemas/',
        params: { limit, offset, sort, sortKey, key, value },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.getSchemas', err)
        }
      },
    }),
    getSchema: builder.query<IFullSchema, IId>({
      async queryFn({ id }, _queryApi, _extraOptions, fetchWithBQ) {
        const result = await fetchWithBQ(`schemas/${id}`)
        const resultScenes = await fetchWithBQ(
          `schemas/${id}/full-schema/scenes`,
        )
        const resultSceneGroups = await fetchWithBQ(
          `schemas/${id}/full-schema/scene-groups`,
        )
        const resultUsers = await fetchWithBQ(`schemas/${id}/full-schema/users`)

        const schemaData = result.data as FullSchemaResponse
        const scenesData = resultScenes.data as { items: SchemaSceneResponse[] }
        const sceneGroupsData = resultSceneGroups.data as {
          items: SchemaSceneGroupResponse[]
        }
        const usersData = resultUsers.data as { items: SchemaUserResponse[] }

        const schema = {
          schema: schemaData,
          scenes: scenesData.items,
          sceneGroups: sceneGroupsData.items,
          users: usersData.items,
        }

        return schema
          ? { data: schema as IFullSchema }
          : { error: result.error as FetchBaseQueryError }
      },
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.getSchemas', err)
        }
      },
    }),
    addSchema: builder.query<void, ISchemaCreate>({
      query: ({ name, startDate, endDate }) => ({
        url: 'schemas/',
        method: 'POST',
        body: {
          name: name.trim(),
          startDate,
          endDate,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.addSchema', err)
        }
      },
    }),
    updateSchema: builder.query<void, ISchemaUpdate & IId>({
      query: ({ id, name, startDate, endDate }) => ({
        url: `schemas/${id}`,
        method: 'PATCH',
        body: {
          name: name.trim(),
          startDate,
          endDate,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.updateSchema', err)
        }
      },
    }),
    deleteSchema: builder.query<void, IId>({
      query: ({ id }) => ({
        url: `schemas/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.deleteSchema', err)
        }
      },
    }),
    requestSubmission: builder.query<
      void,
      { id: number; requestedSubmissionDate: string }
    >({
      query: ({ id, requestedSubmissionDate }) => ({
        url: `schemas/${id}/request-submission`,
        method: 'POST',
        body: {
          requestedSubmissionDate,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.requestSubmission', err)
        }
      },
    }),
    updateDayComment: builder.query<void, IUpdateDay & ISchemaId>({
      query: ({ schemaId, date, comment }) => ({
        url: `schemas/${schemaId}/days`,
        method: 'PATCH',
        body: {
          date,
          comment,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.updateDayComment', err)
        }
      },
    }),
    addUsersToSchema: builder.query<void, IId & { userIds: number[] }>({
      query: ({ id, userIds }) => ({
        url: `schemas/${id}/users`,
        method: 'POST',
        body: {
          ids: userIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.addUsersToSchema', err)
        }
      },
    }),
    removeUserFromSchema: builder.query<void, IId & { userId: number }>({
      query: ({ id, userId }) => ({
        url: `schemas/${id}/users/${userId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.removeUserFromSchema', err)
        }
      },
    }),
    getFileDownloadLink: builder.query<
      DownloadLinkResponse,
      IId & { path: string }
    >({
      query: ({ id, path }) => ({
        url: `schemas/${id}/download-link?path=${path}`,
        method: 'GET',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.getFileDownloadLink', err)
        }
      },
    }),
  }),
})

export const {
  useLazyGetSchemaQuery,
  useLazyGetSchemasQuery,
  useLazyAddSchemaQuery,
  useLazyUpdateSchemaQuery,
  useLazyDeleteSchemaQuery,
  useLazyRequestSubmissionQuery,
  useLazyUpdateDayCommentQuery,
  useLazyAddUsersToSchemaQuery,
  useLazyRemoveUserFromSchemaQuery,
  useLazyGetFileDownloadLinkQuery,
} = schemasApi
